'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getBootstrapConfig } from '#pplx/frontend/apps/ask/bootstrap-config/bootstrapConfig.js';
import { cometAdapter, getCometProvider, getEntropyBrowserContext, getMcpAdapterContext, mcpAdapter, useCometState, } from '#pplx/frontend/apps/ask/comet-browser/providers/EntropyBrowserHooks.js';
import { restoreSidecarThreadsFromCache, saveSidecarThreadsToCache, tryNavigateToLastSidecarThreadForSourceTab, } from '#pplx/frontend/apps/ask/comet-shared/cometUtils.js';
import { useCometMcpEnabledVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useCometMcpEnabledVariation.js';
import { terminateEntry } from '#pplx/frontend/apps/ask/queries/entry-queries/terminateEntry.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
import { MCP_TOOLS_SETTINGS_LS_KEY } from '#pplx/frontend/comet/client/consts.js';
import { isNativeCapabilityCookieSet } from '#pplx/frontend/comet/client/nativeClientCapabilities.js';
import { getSidecarThreadId, getSidecarThreadIdsForSplitView, isCometBrowser, syncInlineAssistantParamsFromURL, } from '#pplx/frontend/comet/client/utils.js';
import { getWindowOrigin } from '#pplx/frontend/common/location.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { redirectWindow } from '#pplx/frontend/platform-components/redirectWindow.js';
// MCPServerType is inlined as string union in strict proto types
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { useIsInSpa, useRouter } from '#pplx/frontend/router/provider.js';
import { isEqual } from 'lodash-es';
import React, { useEffect, useLayoutEffect, useMemo } from 'react';
const CometProvider = getCometProvider();
const EntropyBrowserContext = getEntropyBrowserContext();
const McpAdapterContext = getMcpAdapterContext();
export const EntropyBrowserProvider = ({ children }) => {
    const router = useRouter();
    const isInSpa = useIsInSpa();
    const isComet = isCometBrowser();
    const { session, status: sessionStatus } = useSession();
    // Early read of available MCP tools. In mobile-sidecar, we don't have time to wait for this info
    // from the bridge's back-and-forth event-based async data exchange.
    // Using useLayoutEffect to ensure this runs synchronously before subscribeCometNavigate.
    const isNativeCapabilityCookieSetValue = isNativeCapabilityCookieSet();
    useLayoutEffect(() => {
        if (isNativeCapabilityCookieSetValue && window.initialMCPTools != undefined) {
            mcpAdapter.initializeFromWindowTools(window.initialMCPTools);
            window.initialMCPTools = undefined;
        }
    }, [isNativeCapabilityCookieSetValue]);
    const userId = session?.user?.id;
    const subscriptionTier = session?.user?.subscription_tier;
    useEffect(() => {
        if (!isComet)
            return;
        switch (sessionStatus) {
            case 'authenticated':
                if (!userId)
                    return;
                return cometAdapter.notifyUserStateChanged({
                    status: sessionStatus,
                    userId,
                    subscriptionTier: subscriptionTier ?? 'free',
                });
            case 'unauthenticated':
                return cometAdapter.notifyUserStateChanged({
                    status: sessionStatus,
                });
        }
    }, [sessionStatus, userId, subscriptionTier, isComet]);
    // We use useMemo to run this synchronously during render,
    // so dependent children don't see undefined like they would with a post-render useEffect.
    useMemo(() => {
        if (import.meta.env.VITE_IS_INLINE_ASSISTANT_BUILD) {
            syncInlineAssistantParamsFromURL({ cometAdapter: cometAdapter });
        }
    }, []);
    useLayoutEffect(() => {
        const bootstrapConfig = getBootstrapConfig();
        // Semi-hacky: We're doing this here bc hydration issue might wipe out the data-erp attribute
        if (bootstrapConfig.erp) {
            document.documentElement.dataset.erp = bootstrapConfig.erp;
        }
        const unsubscribeNavigate = cometAdapter.subscribeCometNavigate(({ url }) => {
            // Try to do soft-navigation in SPA
            if (url.origin === getWindowOrigin({ intent: 'document' }) &&
                url.pathname.startsWith(router.base)) {
                url.pathname = url.pathname.replace(router.base, '');
                if (isInSpa(url.pathname)) {
                    const searchParams = url.searchParams.toString();
                    const path = `${url.pathname}${searchParams ? `?${searchParams}` : ''}`;
                    router.push(path, undefined, 'Comet navigate');
                    return;
                }
            }
            // Do hard-navigation for other navigations
            if (import.meta.env.VITE_IS_SIDECAR_BUILD ||
                import.meta.env.VITE_IS_EMBEDDED_SIDECAR_BUILD ||
                import.meta.env.VITE_IS_INLINE_ASSISTANT_BUILD) {
                logger.error('Invalid soft navigation in Sidecar', { url });
                cometAdapter.openTab({ url: url.toString() });
                return;
            }
            const urlString = url.toString();
            redirectWindow(urlString, 'Comet navigate');
        });
        // ! Next part is the only sidecar SPA navigation logic between different tabs
        if (!import.meta.env.VITE_IS_SIDECAR_BUILD) {
            return unsubscribeNavigate;
        }
        const store = cometAdapter.getStore();
        const interval = setInterval(() => {
            const state = store.getState().sidecarUrlById;
            state.forEach((value, sidecarThreadId) => {
                if (value.updatedAt < Date.now() - 1000 * 60 * 60 * 24 * 7) {
                    // Remove stale entries older than 7 days
                    store.getState().actions.deleteSidecarUrlById(sidecarThreadId);
                }
            });
            saveSidecarThreadsToCache(store);
        }, 1000 * 60 * 60 * 6); // 6h
        restoreSidecarThreadsFromCache(store);
        tryNavigateToLastSidecarThreadForSourceTab(store, router);
        let currentSidecarSourceTab = store.getState().sidecarSourceTab;
        const unsubscribeSidecarSourceTab = store.subscribe((state, prevState) => {
            // Use deep comparison to prevent false triggers
            if (isEqual(state.sidecarSourceTab, prevState.sidecarSourceTab)) {
                return;
            }
            const newSidecarSourceTab = state.sidecarSourceTab;
            const oldKey = getSidecarThreadId(currentSidecarSourceTab.tabId, currentSidecarSourceTab.secondaryTab?.tabId);
            const newKey = getSidecarThreadId(newSidecarSourceTab.tabId, newSidecarSourceTab.secondaryTab?.tabId);
            if (newKey === oldKey) {
                return;
            }
            // Commit current pathname to current sidecar source tab
            const currentPathname = new URL(window.location.href).pathname;
            // We want to overwrite both sidecar tabs to same thread if they're in split-view
            const keys = getSidecarThreadIdsForSplitView(currentSidecarSourceTab.tabId, currentSidecarSourceTab.secondaryTab?.tabId);
            keys.forEach((item) => {
                store.getState().actions.setSidecarUrlById(item, currentPathname);
            });
            const updatedState = store.getState().sidecarUrlById;
            const url = updatedState.get(newKey)?.url ?? (router.base || '/');
            currentSidecarSourceTab = newSidecarSourceTab;
            if (url === currentPathname)
                return;
            router.replace(url, 'Sidecar source tab changed');
            saveSidecarThreadsToCache(store);
        });
        return () => {
            clearInterval(interval);
            unsubscribeNavigate();
            unsubscribeSidecarSourceTab();
        };
    }, [router, isInSpa]);
    useEffect(() => {
        return cometAdapter.subscribeToSidecarBrowserTaskStop((entryId) => {
            if (!entryId)
                return;
            terminateEntry({
                entryUUID: entryId,
                reason: 'terminate-from-comet-adapter',
            });
            cometAdapter.cleanupTasks(entryId, 'query_stopped');
        });
    }, []);
    const { variation: cometMcpEnabledVariation } = useCometMcpEnabledVariation({
        defaultValue: false,
    });
    const enableMCPToolbox = isComet ? cometMcpEnabledVariation : isNativeCapabilityCookieSetValue;
    return (_jsx(CometProvider, { children: _jsx(EntropyBrowserContext.Provider, { value: cometAdapter, children: _jsxs(McpAdapterContext.Provider, { value: mcpAdapter, children: [children, enableMCPToolbox && _jsx(CometMcpToolbox, {})] }) }) }));
};
const CometMcpToolbox = () => {
    const cometState = useCometState();
    // Load MCP stdio servers and DXTs on start up
    useEffect(() => {
        mcpAdapter.refreshDxtAndMcpState().catch(() => {
            // suppress error from MCP
        });
    }, []);
    useEffect(() => {
        if (cometState.mcpDisabledServers)
            return;
        try {
            const raw = SafeLocalStorage.getItem('mcp_disabled_servers_v1');
            const parsed = raw ? JSON.parse(raw) : [];
            cometState.actions.setMcpDisabledServers(new Set(parsed));
        }
        catch {
            cometState.actions.setMcpDisabledServers(new Set());
        }
    }, [cometState.mcpDisabledServers, cometState.actions]);
    useEffect(() => {
        if (cometState.mcpToolsSettings)
            return;
        try {
            const raw = SafeLocalStorage.getItem(MCP_TOOLS_SETTINGS_LS_KEY);
            const parsed = raw ? JSON.parse(raw) : {};
            cometState.actions.setMcpToolsSettings(parsed);
        }
        catch {
            cometState.actions.setMcpToolsSettings({});
        }
    }, [cometState.mcpToolsSettings, cometState.actions]);
    return (_jsx(_Fragment, { children: cometState.mcpStdioServers?.map((server) => (_jsx(CometMcpServerItemToolbox, { server: server }, server.name))) }));
};
const CometMcpServerItemToolbox = ({ server }) => {
    const cometState = useCometState();
    const { data: tools } = useQuery({
        queryKey: QueryClientHelper.makeQueryKey('comet/mcp_tools', server.name),
        queryFn: async () => {
            const tools = await mcpAdapter.getMcpTools(server.name).catch(() => []);
            return tools.map((tool) => ({
                ...tool,
                mcp_server: server.name,
                mcp_server_type: 'MCP_SERVER_TYPE_LOCAL',
                app: server.name,
            }));
        },
        enabled: !!server.name,
    });
    useEffect(() => {
        if (!tools)
            return;
        cometState.actions.setMcpTools(server.name, tools);
        return () => {
            cometState.actions.removeMcpTools(server.name);
        };
    }, [tools, cometState.actions, server.name]);
    return null;
};
//# sourceMappingURL=EntropyBrowserProvider.js.map